import React, {
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import moment from 'moment';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import {
  AccountBalanceWalletOutlined,
  AddLinkOutlined,
  AdsClickOutlined,
  AnalyticsOutlined,
  ArticleOutlined,
  ArticleSharp,
  AssessmentOutlined,
  AttachMoneyOutlined,
  AutoGraphOutlined,
  BarChart,
  CampaignOutlined,
  CheckCircleOutlined,
  CompareArrowsOutlined,
  RssFeedOutlined,
  DynamicFormOutlined,
  EmailOutlined,
  ExpandLess,
  ExpandMore,
  FactCheckOutlined,
  GroupsOutlined,
  HistoryOutlined,
  MarkEmailReadOutlined,
  HomeOutlined,
  ImportExportOutlined,
  InsightsOutlined,
  InventoryOutlined,
  ListAltOutlined,
  NewspaperOutlined,
  NotificationsOutlined,
  PaidOutlined,
  PeopleOutlined,
  SchoolOutlined,
  Search,
  SellOutlined,
  SendOutlined,
  SettingsOutlined,
  WebOutlined,
  ShowChartOutlined,
  ShoppingCartOutlined,
  StarBorderOutlined,
  StarOutlined,
  StartOutlined,
  TagOutlined,
  TimelineOutlined,
  TrendingUpOutlined,
  SmartToyOutlined,
  WarningAmberOutlined,
  LiveTvOutlined,
  WavingHandOutlined,
} from '@mui/icons-material';
import FormatListNumberedRtlIcon from '@mui/icons-material/FormatListNumberedRtl';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import LeaderboardOutlined from '@mui/icons-material/LeaderboardOutlined';
import EuroOutlined from '@mui/icons-material/EuroOutlined';
import AltRouteOutlined from '@mui/icons-material/AltRouteOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
// --- P5 ---
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import HandymanOutlined from '@mui/icons-material/HandymanOutlined';
import { UserContext } from '../../contexts/UserContext';

// ─── Menu data structure ───────────────────────────────────────────────────────

interface MenuItemData {
  title: string;
  icon: ReactElement;
  to: string;
  /**
   * Eigene Gruppe des Eintrags, wenn er nicht der Gruppe des Abschnitts folgt (P5: „Alte Auswertungen" mischt
   * fit_products- und webinars_mailings-Seiten). Ohne Angabe gilt `MenuGroup.groupKey`.
   */
  groupKey?: string;
  /**
   * Weitere Suchwörter (P5-Review #19): alte Seitennamen und Stichwörter, unter denen man die Seite sucht — z. B. findet
   * „Kündigung" die Umsatz-Seite, deren Unterseite die alten Kündigungsquoten sind.
   */
  suchwoerter?: string[];
}

interface MenuGroup {
  label: string;
  /** Kurzname für das Etikett im Schnellzugriff (P5-Review #19: der lange Name schnitt den Seitentitel ab). */
  kurz?: string;
  groupKey: string;
  icon: ReactElement;
  items: MenuItemData[];
}

const menuGroups: MenuGroup[] = [
  {
    label: 'Übersicht',
    groupKey: 'overview',
    icon: <HomeOutlined />,
    items: [
      { title: 'Dashboard', icon: <HomeOutlined />, to: '/' },
      {
        title: 'Daily Controlling',
        icon: <AssessmentOutlined />,
        to: '/daily-controlling',
      },
    ],
  },
  {
    label: 'Leads & DOI',
    groupKey: 'leads_doi',
    icon: <CheckCircleOutlined />,
    items: [
      {
        title: 'Traffic Leads',
        icon: <AutoGraphOutlined />,
        to: '/traffic-leads',
      },
      { title: 'Verkaufte Leads', icon: <SellOutlined />, to: '/sold-leads' },
      {
        title: 'Exportierte Leads',
        icon: <SendOutlined />,
        to: '/leads/exports',
      },
      {
        title: 'DOI-Auswertung',
        icon: <CheckCircleOutlined />,
        to: '/doi-evaluation',
      },
      {
        title: 'DOI (Opt-in Emails)',
        icon: <EmailOutlined />,
        to: '/doi-evaluation-optin-emails',
      },
      {
        title: 'DOI Process Details',
        icon: <FactCheckOutlined />,
        to: '/doi-process-details',
      },
      {
        title: 'DOI (Affiliates)',
        icon: <GroupsOutlined />,
        to: '/doi-evaluation-affiliates',
      },
      {
        title: 'DOI (Adref)',
        icon: <TagOutlined />,
        to: '/doi-evaluation-adref',
      },
      {
        title: 'Co-Reg Effizienz',
        icon: <InsightsOutlined />,
        to: '/coreg-efficiency',
      },
    ],
  },
  {
    label: 'AdServer',
    groupKey: 'adserver',
    icon: <BarChart />,
    items: [
      {
        title: 'Report Statistik',
        icon: <BarChart />,
        to: '/adserver/product-statistics',
      },
      {
        title: 'Welcome-Flows',
        icon: <StartOutlined />,
        to: '/adserver/welcome-flows',
      },
      {
        title: 'Ad Form Auswertung',
        icon: <DynamicFormOutlined />,
        to: '/adserver/ad-forms',
      },
      {
        title: 'SAM Scheduling',
        icon: <CampaignOutlined />,
        to: '/adserver/sam-scheduling',
      },
      {
        title: 'Send Pauses',
        icon: <CalendarTodayOutlined />,
        to: '/adserver/send-pauses',
      },
      {
        title: 'Ext. SAM Config',
        icon: <DynamicFormOutlined />,
        to: '/adserver/external-sam-config',
      },
    ],
  },
  {
    label: 'E-Mail Marketing',
    groupKey: 'email_marketing',
    icon: <CampaignOutlined />,
    items: [
      {
        title: 'List-Entwicklung',
        icon: <TimelineOutlined />,
        to: '/mailchimp',
      },
      {
        title: 'Ext. Kunden Stats',
        icon: <GroupsOutlined />,
        to: '/mailchimp/client-stats',
      },
      {
        title: 'Iterable Statistiken',
        icon: <PeopleOutlined />,
        to: '/iterable-statistics',
      },
      {
        title: 'Web Push',
        icon: <NotificationsOutlined />,
        to: '/web-push',
      },
      {
        title: 'Newsletter Kalender',
        icon: <CalendarTodayOutlined />,
        to: '/calendar',
      },
      {
        title: 'Newsletter Auswertung',
        icon: <AnalyticsOutlined />,
        to: '/newsletter',
      },
      {
        title: 'SAM Timeline',
        icon: <CampaignOutlined />,
        to: '/sam-timeline',
      },
      {
        title: 'NL Ads Timeline',
        icon: <NewspaperOutlined />,
        to: '/newsletter-ads-timeline',
      },
      {
        title: 'KI Ad Insertions',
        icon: <SmartToyOutlined />,
        to: '/newsletter-ad-insertions',
      },
      {
        title: 'Ad Statistiken',
        icon: <InsightsOutlined />,
        to: '/ad-statistics',
      },
      {
        title: 'Newsletter Redlinks',
        icon: <AddLinkOutlined />,
        to: '/newsletter-redlinks',
      },
      {
        title: 'ETKP Entwicklung',
        icon: <ShowChartOutlined />,
        to: '/etkp-development',
      },
      {
        title: 'E-Mail Verlauf',
        icon: <HistoryOutlined />,
        to: '/email-history',
      },
      {
        title: 'Deliverability',
        icon: <MarkEmailReadOutlined />,
        to: '/email-deliverability',
      },
      {
        title: 'DNV Migration',
        icon: <ImportExportOutlined />,
        to: '/dnv-migration',
      },
      {
        title: 'Welcome-Kampagne',
        icon: <WavingHandOutlined />,
        to: '/welcome-campaign',
      },
    ],
  },
  {
    label: 'Umsatz & VNR',
    groupKey: 'revenue_vnr',
    icon: <AttachMoneyOutlined />,
    items: [
      {
        title: 'VNR-Umsätze',
        icon: <TrendingUpOutlined />,
        to: '/vnr-revenue',
      },
      {
        title: 'ROI-Auswertung',
        icon: <AttachMoneyOutlined />,
        to: '/vnr/roi',
      },
      {
        title: 'ROI-Tests (A/B)',
        icon: <TrendingUpOutlined />,
        to: '/vnr/roi-tests',
      },
      {
        title: 'Lead Snapshots',
        icon: <ListAltOutlined />,
        to: '/vnr/snapshot-evaluation',
      },
    ],
  },
  {
    // Sektion „Anfos & Umsatz" (Plan 2026-10-06): löst die Auswertungsseiten unter FIT Produkte ab.
    label: 'Anfos & Umsatz',
    groupKey: 'fit_products',
    icon: <InsightsOutlined />,
    items: [
      {
        title: 'Übersicht & Definitionen',
        icon: <MenuBookOutlined />,
        to: '/auswertungen',
      },
      // Reihenfolge = Kapitel-Liste der Übersicht (`auswertungen/index.tsx` KAPITEL, P5-Review #18; Audit 30.10.2026 E1):
      // Bestellungen zuerst, Werkzeuge zuletzt.
      {
        title: 'Bestellungen',
        icon: <ReceiptLongOutlined />,
        to: '/auswertungen/bestellungen',
        suchwoerter: ['Anfo-Übersicht', 'Anfos', 'PLX', 'PL-X', 'plx-orders', 'Bestellliste', 'verweigert'],
      },
      {
        title: 'Webinare & Produkte',
        icon: <LeaderboardOutlined />,
        to: '/auswertungen/webinare-produkte',
        suchwoerter: ['Webinar-Auswertung', 'Webinar Auswertungen', 'Webinar Verhalten', 'Produkte', 'Ranking'],
      },
      {
        title: 'Umsatz & Bezahlqualität',
        icon: <EuroOutlined />,
        to: '/auswertungen/umsatz',
        suchwoerter: [
          'Kündigungsquoten',
          'Cashflow Prognose',
          'Umsatz-Diskrepanzen',
          'Diskrepanz',
          'Anfo-Bewertung',
          'Anfo-Wert',
          'Anfo Statistiken',
          'Bezahlquote',
        ],
      },
      {
        title: 'Kanäle & Werbeflächen',
        icon: <AltRouteOutlined />,
        to: '/auswertungen/kanaele',
        suchwoerter: ['Quelle', 'Blog', 'Banner', 'Werbefläche'],
      },
      {
        title: 'Hebel',
        icon: <TuneOutlined />,
        to: '/auswertungen/hebel',
        suchwoerter: ['Heat-Up', 'Heatup', 'Wellen', 'Nachfass', 'Cross-Sell', 'Crosssell', 'Bestandskunden'],
      },
      {
        title: 'Werkzeuge',
        icon: <HandymanOutlined />,
        to: '/auswertungen/werkzeuge',
        // Die Reiternamen wörtlich (Suche ist ein Teilwort-Vergleich: „Formelblatt" steckt nicht in „Formeln").
        suchwoerter: [
          'Webinar User Mappings',
          'Webinar-Zuordnungen',
          'Zuordnungen',
          'Affiliate-Gruppierungen',
          'Affiliate Gruppierungen',
          'Design-A/B',
          'Formelblatt',
          'Formeln',
        ],
      },
    ],
  },
  {
    label: 'FIT Produkte',
    groupKey: 'fit_products',
    icon: <ShoppingCartOutlined />,
    items: [
      // P5 (27.10.2026): Anfo-Übersicht, Umsatz-Diskrepanzen, Anfo-Bewertung, Kündigungsquoten und Cashflow leiten in die
      // Sektion „Anfos & Umsatz" um (Bestellungen bzw. Umsatz-Unterseiten); die Pflegeseiten bleiben hier.
      {
        title: 'Anfo-Konfiguration',
        icon: <SettingsOutlined />,
        to: '/plx-orders/config',
      },
      {
        title: 'Produkte verwalten',
        icon: <InventoryOutlined />,
        to: '/plx-orders/products',
      },
      {
        title: 'Anfo-Gruppierungen',
        icon: <FormatListNumberedRtlIcon />,
        to: '/plx-orders/groupings',
      },
      {
        title: 'PX-Data Import',
        icon: <ImportExportOutlined />,
        to: '/plx-orders/px-import',
      },
      {
        title: 'Wert je FT-Anmeldung',
        icon: <AccountBalanceWalletOutlined />,
        to: '/ft-lifetime-value',
      },
    ],
  },
  {
    // --- P5 --- Alt-Seiten, deren Funktionen noch nicht alle in der Sektion stehen (Till 27.10.2026): erreichbar mit
    // Hinweis „wird abgelöst", bis Till sie je Seite abnimmt. Einträge tragen ihre eigene Gruppe.
    label: 'Alte Auswertungen (bis Abnahme)',
    kurz: 'alt',
    groupKey: 'fit_products',
    icon: <HistoryOutlined />,
    items: [
      {
        title: 'Anfo-Übersicht (alt)',
        icon: <ShoppingCartOutlined />,
        to: `/plx-orders/alt?startDate=${moment().subtract(3, 'month').format('YYYY-MM-DD')}`,
        groupKey: 'fit_products',
      },
      {
        title: 'Anfo Statistiken',
        icon: <TrendingUpOutlined />,
        to: '/anfo-statistics',
        groupKey: 'fit_products',
      },
      {
        title: 'Webinar Auswertungen',
        icon: <AssessmentOutlined />,
        to: `/webinar-auswertungen?startDate=${moment().subtract(3, 'months').format('YYYY-MM-DD')}`,
        groupKey: 'webinars_mailings',
      },
      {
        title: 'Webinar Verhalten',
        icon: <SchoolOutlined />,
        to: `/webinar-user-verhalten?startDate=${moment().subtract(1, 'year').format('YYYY-MM-DD')}`,
        groupKey: 'webinars_mailings',
      },
    ],
  },
  {
    label: 'Webinare & Mailings',
    groupKey: 'webinars_mailings',
    icon: <SchoolOutlined />,
    items: [
      // P5: „Webinar Verhalten" und „Webinar Auswertungen" stehen unter „Alte Auswertungen (bis Abnahme)".
      {
        title: 'Webinar Mailings',
        icon: <CampaignOutlined />,
        to: '/webinar-mailings',
      },
      {
        title: 'Webinar Management',
        icon: <SettingsOutlined />,
        to: '/webinar-management',
      },
      {
        title: 'Webinar LPs',
        icon: <WebOutlined />,
        to: '/webinar-lp',
      },
      {
        title: 'Webinarverteiler',
        icon: <CampaignOutlined />,
        to: '/webinar-verteiler',
      },
      {
        title: 'Direkt-Mailings',
        icon: <CampaignOutlined />,
        to: '/plx-mailings',
      },
      {
        title: 'DOI/Website Ads',
        icon: <AdsClickOutlined />,
        to: '/ad-evaluation',
      },
    ],
  },
  {
    label: 'Content & Partner',
    groupKey: 'content_partners',
    icon: <ArticleOutlined />,
    items: [
      {
        title: 'Artikel-Gruppierung',
        icon: <ArticleOutlined />,
        to: '/redaktion',
      },
      {
        title: 'Artikel-Evaluierung',
        icon: <AssessmentOutlined />,
        to: '/redaktion/artikel-auswertung',
      },
      {
        title: 'Lead Wall A/B',
        icon: <CompareArrowsOutlined />,
        to: '/redaktion/leadwall',
      },
      {
        title: 'Lead Wall Akquise-Test',
        icon: <CompareArrowsOutlined />,
        to: '/redaktion/leadwall-channel-test',
      },
      {
        title: 'Push Auswertung',
        icon: <NotificationsOutlined />,
        to: '/push',
      },
      {
        title: 'BG Artikel/Leads',
        icon: <ArticleSharp />,
        to: '/boerse-global/article-distribution',
      },
      {
        title: 'BG Lead Umsatz',
        icon: <PaidOutlined />,
        to: '/gurupress/lead-turnover',
      },
      {
        title: 'BG Lead Imports',
        icon: <ImportExportOutlined />,
        to: '/gurupress/lead-imports',
      },
    ],
  },
  {
    label: 'MSM Partner',
    groupKey: 'msm_partner',
    icon: <SendOutlined />,
    items: [
      {
        title: 'MSM Partner-Dashboard',
        icon: <InsightsOutlined />,
        to: '/msm-partner',
      },
    ],
  },
  {
    label: 'NFA Extern',
    groupKey: 'nfa_external',
    icon: <SendOutlined />,
    items: [
      {
        title: 'NFA Mailings',
        icon: <EmailOutlined />,
        to: '/nfa-mailings',
      },
      {
        title: 'DNV Asset-Analyse',
        icon: <EmailOutlined />,
        to: '/dnv-asset-analysis',
      },
    ],
  },
  {
    label: 'Traffic & SEO',
    groupKey: 'ga4_traffic',
    icon: <TrendingUpOutlined />,
    items: [
      {
        title: 'Portal-Traffic',
        icon: <TrendingUpOutlined />,
        to: '/ga4-traffic',
      },
    ],
  },
  {
    label: 'Broker Hub',
    groupKey: 'broker_hub',
    icon: <AttachMoneyOutlined />,
    items: [
      {
        title: 'Broker-Profile',
        icon: <CompareArrowsOutlined />,
        to: '/broker-hub',
      },
      {
        title: 'Broker-Content',
        icon: <RssFeedOutlined />,
        to: '/broker-content',
      },
    ],
  },
  {
    label: 'Live Streaming',
    groupKey: 'live',
    icon: <LiveTvOutlined />,
    items: [
      {
        title: 'Live Admin',
        icon: <LiveTvOutlined />,
        to: '/live-admin',
      },
    ],
  },
  {
    label: 'Administration',
    groupKey: 'administration',
    icon: <SettingsOutlined />,
    items: [
      {
        title: 'Berechtigungen',
        icon: <SettingsOutlined />,
        to: '/admin/allowed-emails',
      },
      {
        title: 'Lead Source Rules',
        icon: <FactCheckOutlined />,
        to: '/settings/lead-source-rules',
      },
      {
        title: 'Fehler-Log',
        icon: <WarningAmberOutlined />,
        to: '/admin/error-log',
      },
    ],
  },
];

// Flatten for search + favorites lookup
const allItems = menuGroups.flatMap((g) => g.items.map((item) => ({ ...item, group: g.label })));
const groupKurz: Record<string, string | undefined> = Object.fromEntries(menuGroups.map((g) => [g.label, g.kurz]));

// Build path → title mapping for active state detection
const pathToTitle: Record<string, string> = {};
for (const item of allItems) {
  // Strip query params for matching
  const path = item.to.split('?')[0];
  pathToTitle[path] = item.title;
}
// Unterseiten ohne eigenen Menüeintrag, deren Elternpfad ein ANDERER Eintrag ist: die Webinar-
// Detailseite der Sektion gehört zu „Webinare & Produkte", nicht zur Übersicht unter /auswertungen.
pathToTitle['/auswertungen/webinare'] = 'Webinare & Produkte';

// Build title → group label mapping
const titleToGroup: Record<string, string> = {};
for (const item of allItems) {
  titleToGroup[item.title] = item.group;
}

// ─── localStorage helpers ──────────────────────────────────────────────────────

const STORAGE_KEY_FAVORITES = 'sidebar_favorites';
const STORAGE_KEY_OPEN_GROUPS = 'sidebar_open_groups';
const STORAGE_KEY_COLLAPSED = 'sidebar_collapsed';

const DEFAULT_FAVORITES = [
  'Dashboard',
  'Daily Controlling',
  // P5: „Anfo-Übersicht" ist in „Bestellungen" (Sektion) aufgegangen, die alte Webinar-Auswertung in „Webinare & Produkte".
  'Bestellungen',
  'Webinar Mailings',
  'Webinare & Produkte',
  'Direkt-Mailings',
];

/**
 * Favoriten sind nach TITEL gespeichert (P5-Review #19): umbenannte Seiten fielen still aus dem Schnellzugriff. Alter
 * Titel → heutiger Eintrag; mehrere alte Titel auf denselben Eintrag werden zu einem.
 */
export const FAVORITEN_UMBENANNT: Record<string, string> = {
  'Anfo-Übersicht': 'Bestellungen',
  'Kündigungsquoten': 'Umsatz & Bezahlqualität',
  'Cashflow Prognose': 'Umsatz & Bezahlqualität',
  'Umsatz-Diskrepanzen': 'Umsatz & Bezahlqualität',
  'Anfo-Bewertung': 'Umsatz & Bezahlqualität',
};

export function migriereFavoriten(gespeichert: string[]): string[] {
  const out: string[] = [];
  for (const t of gespeichert) {
    const neu = FAVORITEN_UMBENANNT[t] ?? t;
    if (!out.includes(neu)) out.push(neu);
  }
  return out;
}

function loadFavorites(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_FAVORITES);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) return migriereFavoriten(parsed.filter((t): t is string => typeof t === 'string'));
    }
  } catch {
    // ignore
  }
  return DEFAULT_FAVORITES;
}

function saveFavorites(favorites: string[]) {
  localStorage.setItem(STORAGE_KEY_FAVORITES, JSON.stringify(favorites));
}

function loadOpenGroups(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_OPEN_GROUPS);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return ['Übersicht'];
}

function saveOpenGroups(groups: string[]) {
  localStorage.setItem(STORAGE_KEY_OPEN_GROUPS, JSON.stringify(groups));
}

function loadCollapsed(): boolean {
  const stored = localStorage.getItem(STORAGE_KEY_COLLAPSED);
  if (stored !== null) return stored === 'true';
  // No stored choice: start collapsed below md (900 px). Expanded, the sidebar took 270 of 390 px on a phone and
  // pushed every page into horizontal scroll (measured 2026-10-20).
  return window.innerWidth < 900;
}

function saveCollapsed(collapsed: boolean) {
  localStorage.setItem(STORAGE_KEY_COLLAPSED, String(collapsed));
}

// ─── Sidebar Component ─────────────────────────────────────────────────────────

const Sidebar: FC = () => {
  const location = useLocation();
  const userInfo = useContext(UserContext);
  const [isCollapsed, setIsCollapsed] = useState(loadCollapsed);
  const [search, setSearch] = useState('');
  const [favorites, setFavorites] = useState<string[]>(loadFavorites);
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(loadOpenGroups()));

  // Filter menu groups based on user permissions
  const visibleGroups = useMemo(() => {
    if (!userInfo || userInfo.allowed_groups === null) return menuGroups;
    const erlaubt = userInfo.allowed_groups;
    // Je Eintrag seine eigene Gruppe (P5), sonst die des Abschnitts; leere Abschnitte fallen weg.
    return menuGroups
      .map((g) => ({ ...g, items: g.items.filter((i) => erlaubt.includes(i.groupKey ?? g.groupKey)) }))
      .filter((g) => g.items.length > 0);
  }, [userInfo]);

  // Visible items for search + favorites
  const visibleItems = useMemo(
    () => visibleGroups.flatMap((g) => g.items.map((item) => ({ ...item, group: g.label }))),
    [visibleGroups],
  );

  // Determine active item from current path
  const activeTitle = useMemo(() => {
    const pathname = location.pathname;
    // Try exact match first (without trailing slash)
    const normalized = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
    if (pathToTitle[normalized]) return pathToTitle[normalized];
    // Try parent path for nested routes like /webinar-auswertungen/:id
    const segments = normalized.split('/').filter(Boolean);
    while (segments.length > 0) {
      const candidate = '/' + segments.join('/');
      if (pathToTitle[candidate]) return pathToTitle[candidate];
      segments.pop();
    }
    return '';
  }, [location.pathname]);

  // Auto-open the group containing the active item
  useEffect(() => {
    if (activeTitle && titleToGroup[activeTitle]) {
      const group = titleToGroup[activeTitle];
      setOpenGroups((prev) => {
        if (prev.has(group)) return prev;
        const next = new Set(prev);
        next.add(group);
        return next;
      });
    }
  }, [activeTitle]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      saveCollapsed(next);
      return next;
    });
  }, []);

  const toggleGroup = useCallback((label: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      saveOpenGroups(Array.from(next));
      return next;
    });
  }, []);

  const toggleFavorite = useCallback((title: string) => {
    setFavorites((prev) => {
      const next = prev.includes(title) ? prev.filter((f) => f !== title) : [...prev, title];
      saveFavorites(next);
      return next;
    });
  }, []);

  const searchLower = search.toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!searchLower) return visibleGroups;
    return visibleGroups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (item) =>
            item.title.toLowerCase().includes(searchLower) ||
            g.label.toLowerCase().includes(searchLower) ||
            (item.suchwoerter ?? []).some((w) => w.toLowerCase().includes(searchLower)),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [searchLower, visibleGroups]);

  const favoriteItems = useMemo(
    () => visibleItems.filter((item) => favorites.includes(item.title)),
    [favorites, visibleItems],
  );

  // ─── Collapsed view: icon rail ───────────────────────────────────────────────

  if (isCollapsed) {
    return (
      <Box
        className="sidebar"
        sx={{
          width: 56,
          minHeight: '100vh',
          bgcolor: '#1F2A40',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 1,
          gap: 0.3,
          borderRight: '1px solid #292929',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <IconButton onClick={toggleCollapsed} sx={{ color: '#e0e0e0', mb: 1 }}>
          <MenuOutlinedIcon />
        </IconButton>
        {visibleGroups.map((group) => (
          <Tooltip key={group.label} title={group.label} placement="right" arrow>
            <IconButton
              onClick={toggleCollapsed}
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.5,
                color: openGroups.has(group.label) ? '#868dfb' : '#858585',
                '&:hover': {
                  bgcolor: 'rgba(104, 112, 250, 0.08)',
                  color: '#868dfb',
                },
              }}
            >
              {group.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    );
  }

  // ─── Expanded view ───────────────────────────────────────────────────────────

  return (
    <Box
      className="sidebar"
      sx={{
        width: 270,
        minWidth: 270,
        minHeight: '100vh',
        bgcolor: '#1F2A40',
        color: '#e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        borderRight: '1px solid #292929',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: '12px 12px 4px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h5" fontWeight="bold" color="#e0e0e0">
          Dashboard
        </Typography>
        <IconButton onClick={toggleCollapsed} sx={{ color: '#858585' }}>
          <MenuOutlinedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ px: 1.5, pb: 1, pt: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Suchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: '#858585' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: 13,
              color: '#e0e0e0',
              bgcolor: 'rgba(255,255,255,0.04)',
              borderRadius: 2,
              height: 34,
              '& fieldset': { borderColor: '#3d3d3d' },
              '&:hover fieldset': { borderColor: '#525252' },
              '&.Mui-focused fieldset': { borderColor: '#6870fa' },
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#858585',
              opacity: 1,
            },
          }}
        />
      </Box>

      {/* Scrollable content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#3d3d3d', borderRadius: 2 },
        }}
      >
        {/* Favorites / Schnellzugriff */}
        {!search && favoriteItems.length > 0 && (
          <>
            <Box
              sx={{
                px: 2,
                pt: 0.8,
                pb: 0.3,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <StarOutlined sx={{ fontSize: 13, color: '#ffa726' }} />
              <Typography
                variant="overline"
                sx={{
                  fontSize: 10,
                  color: '#858585',
                  letterSpacing: 1.2,
                  lineHeight: 1,
                }}
              >
                Schnellzugriff
              </Typography>
            </Box>
            <List disablePadding sx={{ pb: 0.3 }}>
              {favoriteItems.map((item) => (
                <ListItemButton
                  key={`fav-${item.to}`}
                  component={Link}
                  to={item.to}
                  selected={activeTitle === item.title}
                  sx={{
                    py: 0.25,
                    px: 2,
                    mx: 0.5,
                    borderRadius: 1,
                    textDecoration: 'none',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(104, 112, 250, 0.15)',
                      '&:hover': { bgcolor: 'rgba(104, 112, 250, 0.2)' },
                    },
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: activeTitle === item.title ? '#6870fa' : '#858585',
                      minWidth: 26,
                      '& svg': { fontSize: 16 },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontSize: 12,
                      color: activeTitle === item.title ? '#6870fa' : '#c2c2c2',
                      noWrap: true,
                    }}
                  />
                  <Tooltip title={item.group} placement="right" disableHoverListener={!groupKurz[item.group]}>
                    <Chip
                      label={groupKurz[item.group] ?? item.group}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: 9,
                        bgcolor: 'rgba(104, 112, 250, 0.1)',
                        color: '#868dfb',
                        '& .MuiChip-label': { px: 0.6 },
                      }}
                    />
                  </Tooltip>
                </ListItemButton>
              ))}
            </List>
            <Divider sx={{ borderColor: '#292929', mx: 2, my: 0.5 }} />
          </>
        )}

        {/* Groups */}
        <List disablePadding sx={{ pb: 2, pt: search ? 0 : 0.3 }}>
          {filteredGroups.map((group) => {
            const isOpen = search ? true : openGroups.has(group.label);
            return (
              <React.Fragment key={group.label}>
                {/* Group header */}
                <ListItemButton
                  onClick={() => !search && toggleGroup(group.label)}
                  sx={{
                    py: 0.5,
                    px: 2,
                    mx: 0.5,
                    borderRadius: 1,
                    mt: 0.3,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                  }}
                >
                  <ListItemIcon sx={{ color: '#868dfb', minWidth: 28 }}>
                    <Box sx={{ '& svg': { fontSize: 18 } }}>{group.icon}</Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={group.label}
                    primaryTypographyProps={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: '#a3a3a3',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  />
                  {!search &&
                    (isOpen ? (
                      <ExpandLess sx={{ fontSize: 16, color: '#525252' }} />
                    ) : (
                      <ExpandMore sx={{ fontSize: 16, color: '#525252' }} />
                    ))}
                </ListItemButton>

                {/* Group items */}
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {group.items.map((item) => {
                      const isActive = activeTitle === item.title;
                      const isFav = favorites.includes(item.title);
                      return (
                        <ListItemButton
                          key={item.to}
                          component={Link}
                          to={item.to}
                          selected={isActive}
                          sx={{
                            py: 0.3,
                            pl: 4.5,
                            pr: 1,
                            mx: 0.5,
                            borderRadius: 1,
                            textDecoration: 'none',
                            '&.Mui-selected': {
                              bgcolor: 'rgba(104, 112, 250, 0.15)',
                              borderLeft: '2px solid #6870fa',
                              '&:hover': {
                                bgcolor: 'rgba(104, 112, 250, 0.2)',
                              },
                            },
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.04)',
                              '& .fav-icon': { opacity: 1 },
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              color: isActive ? '#6870fa' : '#666666',
                              minWidth: 26,
                              '& svg': { fontSize: 16 },
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{
                              fontSize: 12,
                              color: isActive ? '#6870fa' : '#c2c2c2',
                              noWrap: true,
                            }}
                          />
                          <IconButton
                            className="fav-icon"
                            size="small"
                            aria-label={isFav ? `${item.title} aus Favoriten entfernen` : `${item.title} zu Favoriten`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleFavorite(item.title);
                            }}
                            sx={{
                              opacity: isFav ? 1 : 0,
                              transition: 'opacity 150ms',
                              p: 0.3,
                            }}
                          >
                            {isFav ? (
                              <StarOutlined sx={{ fontSize: 14, color: '#ffa726' }} />
                            ) : (
                              <StarBorderOutlined sx={{ fontSize: 14, color: '#666666' }} />
                            )}
                          </IconButton>
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default Sidebar;
